import styled from '@emotion/styled'
import { Card, Col, Form, Row, Skeleton, Typography, message, Alert } from 'antd'
import { goBack } from 'assets/icons'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store'
import { useEffect, useState } from 'react'
import ManageMerchantForm from './ManageMerchantForm'
import { useGetMerchantList, useGetTpMerchantList, useCreateMerchantPair } from 'hooks/useMerchant'
import { AddressFacData } from 'services/interface.d'

const ManageMerchant = () => {
  const navigate = useNavigate()
  const { contact, profile } = useAppSelector((state) => state.auth)
  const factory_id = profile?.user?.role?.ref_id ?? ''

  const [form] = Form.useForm()
  const [isChanged, setIsChanged] = useState<boolean>(false)

  // ดึง Merchant List — ทั้ง Speed และไปรษณีย์ไทย
  const { data: merchantData, isLoading: speedLoading, refetch: refetchSpeed } = useGetMerchantList(factory_id)
  const { data: tpMerchantData, isLoading: tpLoading, refetch: refetchTp } = useGetTpMerchantList(factory_id)
  const merchantLoading = speedLoading || tpLoading

  // หา Default Merchant ของแต่ละฝั่ง — 2026-09-14: ไม่กรอง is_active ด้วย ให้ตรงกับ unique constraint
  // ระดับ DB จริง (ux_factory_speed_merchant_factory_default_not_deleted scope แค่ is_default +
  // is_delete ไม่สนใจ is_active) และตรงกับเงื่อนไขฝั่ง backend (createFactoryMerchantPair) — ถ้าเช็คด้วย
  // is_active แล้วมีแถวที่ is_default=true แต่ถูก toggle ปิดไว้ (is_active=false) หน้าจะเข้าใจผิดว่ายังไม่มี
  // merchant แล้วปล่อยให้กรอกฟอร์มใหม่ พอ submit ไปก็ไปชน unique constraint ที่ backend จริง
  const defaultMerchant = merchantData?.data?.find(
    (m) => m.is_default === true && m.is_delete === false,
  )
  const defaultTpMerchant = tpMerchantData?.data?.find(
    (m) => m.is_default === true && m.is_delete === false,
  )

  // 2026-09-14: มี merchant อยู่แล้วฝั่งใดฝั่งหนึ่ง (หรือทั้งคู่) = ล็อกฟอร์ม ห้ามแก้ไขเอง ต้องแจ้ง
  // เจ้าหน้าที่แทน — ล็อกตั้งแต่มีแค่ฝั่งเดียวเพราะ backend (createFactoryMerchantPair) ก็ปฏิเสธการสร้าง
  // ถ้ามีฝั่งใดฝั่งหนึ่งอยู่แล้วเหมือนกัน ฟอร์มกับ backend ต้องสอดคล้องกัน
  const isLocked = !!defaultMerchant || !!defaultTpMerchant

  // Mutation สำหรับสร้างทั้งคู่ (ใช้ตอนยังไม่มี merchant เลยทั้งคู่เท่านั้น)
  const { mutate: createPair, isPending } = useCreateMerchantPair(factory_id)

  // โหลดข้อมูลลง Form เมื่อข้อมูลพร้อม — ตอนล็อก แสดงข้อมูลที่มีอยู่ (Speed ก่อน ถ้าไม่มีใช้ไปรษณีย์ไทย)
  // ตอนไม่ล็อก fallback ด้วยที่อยู่โรงงานให้กรอกง่ายขึ้น
  useEffect(() => {
    if (merchantLoading) return

    if (defaultMerchant) {
      form.setFieldsValue({
        line_1: defaultMerchant.address ?? '',
        province_id: Number(defaultMerchant.province_id || contact?.address_fac?.province_id) || undefined,
        district_id: Number(defaultMerchant.district_id || contact?.address_fac?.district_id) || undefined,
        sub_district_id: Number(defaultMerchant.sub_district_id || contact?.address_fac?.sub_district_id) || undefined,
        zip_code: defaultMerchant.postcode || contact?.address_fac?.zip_code || '',
        mobile: defaultMerchant.mobile1 ?? '',
        email: defaultMerchant.email ?? '',
      })
    } else if (defaultTpMerchant) {
      form.setFieldsValue({
        line_1: defaultTpMerchant.merchant_address ?? '',
        zip_code: defaultTpMerchant.merchant_postcode || '',
        mobile: defaultTpMerchant.merchant_phone_number ?? '',
        email: defaultTpMerchant.merchant_email ?? '',
      })
    } else if (contact?.address_fac) {
      // ไม่มี Merchant เลยทั้งคู่ → Fallback ด้วยที่อยู่โรงงาน
      const addr = contact.address_fac
      form.setFieldsValue({
        line_1: addr.line_1 ?? '',
        province_id: addr.province_id ?? undefined,
        district_id: addr.district_id ?? undefined,
        sub_district_id: addr.sub_district_id ?? undefined,
        zip_code: addr.zip_code ?? '',
        mobile: contact.telephone_number ?? '',
        email: '',
      })
    }
  }, [defaultMerchant, defaultTpMerchant, merchantLoading, contact])

  const onFinish = (values: any) => {
    if (!factory_id) {
      message.error('ไม่พบข้อมูลโรงงาน')
      return
    }

    createPair(
      {
        pickup_name: values.pickup_name || 'maindc',
        address: values.line_1 ?? '',
        province_id: Number(values.province_id),
        district_id: Number(values.district_id),
        sub_district_id: Number(values.sub_district_id),
        postcode: String(values.zip_code ?? ''),
        mobile: values.mobile,
        email: values.email ?? '',
      },
      {
        onSuccess: (res) => {
          const { speed_status, tp_status, speed_error, tp_error } = res.data
          const anySuccess = speed_status === 'success' || tp_status === 'success'

          if (speed_status === 'success' && tp_status === 'success') {
            message.success('บันทึกที่อยู่รับสินค้าสำเร็จ')
            setIsChanged(false)
          } else if (speed_status === 'success' && tp_status !== 'success') {
            message.warning(`บันทึกสำเร็จฝั่ง Speed แต่ไปรษณีย์ไทยไม่สำเร็จ (${tp_error ?? 'ไม่ทราบสาเหตุ'}) กรุณาติดต่อเจ้าหน้าที่เพื่อเพิ่มข้อมูลไปรษณีย์ไทยให้`)
          } else if (speed_status !== 'success' && tp_status === 'success') {
            message.warning(`บันทึกสำเร็จฝั่งไปรษณีย์ไทย แต่ Speed ไม่สำเร็จ (${speed_error ?? 'ไม่ทราบสาเหตุ'}) กรุณาติดต่อเจ้าหน้าที่เพื่อเพิ่มข้อมูล Speed ให้`)
          } else {
            message.error('บันทึกที่อยู่รับสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
          }

          // 2026-09-14: สร้างสำเร็จอย่างน้อยฝั่งใดฝั่งหนึ่งแล้ว → refetch ทั้ง 2 list ให้ isLocked เห็นข้อมูล
          // ใหม่ทันที ฟอร์มจะ disabled + ปุ่มบันทึกหายไปเองโดยไม่ต้องรีเฟรชหน้า (ตรงกับ backend ที่ตอนนี้
          // จะปฏิเสธการสร้างซ้ำถ้ามีฝั่งใดฝั่งหนึ่งอยู่แล้วเหมือนกัน — ฟอร์มต้องล็อกทันทีที่มีฝั่งใดฝั่งหนึ่งสำเร็จ)
          if (anySuccess) {
            refetchSpeed()
            refetchTp()
          }
        },
        onError: () => {
          message.error('บันทึกที่อยู่รับสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
        },
      },
    )
  }

  // address_fac สำหรับ FactoryAddress component (ใช้ set Default Dropdown)
  const addressFacForForm: AddressFacData | undefined = defaultMerchant
    ? {
        line_1: defaultMerchant.address ?? '',
        line_2: '',
        province_id: Number(defaultMerchant.province_id || contact?.address_fac?.province_id) || 0,
        province_name: '',
        district_id: Number(defaultMerchant.district_id || contact?.address_fac?.district_id) || 0,
        district_name: '',
        sub_district_id: Number(defaultMerchant.sub_district_id || contact?.address_fac?.sub_district_id) || 0,
        sub_district_name: '',
        zip_code: Number(defaultMerchant.postcode || contact?.address_fac?.zip_code) || 0,
      }
    : contact?.address_fac

  return (
    <>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper onClick={() => navigate('/setting')}>
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>ตั้งค่าที่อยู่รับสินค้า</Typography.Title>
              <RightIconWrapper />
            </HeaderTabs>

            <WrapperAll>
              {merchantLoading ? (
                <Skeleton active paragraph={{ rows: 8 }} />
              ) : (
                <>
                  {isLocked ? (
                    <Alert
                      description="หากต้องการแก้ไขข้อมูลที่อยู่สำหรับให้ขนส่งเข้ารับสินค้า กรุณาแจ้งผู้ดูแลระบบ"
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : (
                    <Alert
                      description="กรอกข้อมูลที่อยู่สำหรับให้ขนส่งพาร์ตเนอร์แบ่งเบาเข้าไปรับสินค้า"
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <ManageMerchantForm
                    form={form}
                    onFinish={onFinish}
                    address_fac={addressFacForForm}
                    isPending={isPending}
                    isChanged={isChanged}
                    onValuesChange={() => setIsChanged(true)}
                    disabled={isLocked}
                  />
                </>
              )}
            </WrapperAll>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ManageMerchant

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 40px;
`

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
`
